import { AddCardDialog, type AddedPaymentMethod } from '@/components/payment-methods/AddCardDialog'
import { describePaymentMethod } from '@/components/payment-methods/PaymentMethodsList'
import { cn, useAuth } from '@/lib'
import { Button, SubtleLoader } from '@/ui'
import { CreditCardIcon } from '@heroicons/react/24/outline'
import { type BillingProfileData, type PaymentMethodData, usePaymentMethodIndex } from '@jobilla/nexus-sdk'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const POLL_INTERVAL_MS = 2_000
const POLL_DURATION_MS = 20_000

export function profileCards(paymentMethods: PaymentMethodData[], profile: BillingProfileData): PaymentMethodData[] {
	return paymentMethods.filter((paymentMethod) =>
		paymentMethod.type === 'card' && paymentMethod.stripeCustomerId === profile.stripeCustomerId
	)
}

interface CardStepProps {
	heading?: ReactNode
	className?: string
	organisationId: string
	profile: BillingProfileData
	selectedId: string | null
	onSelect: (paymentMethodId: string) => void
	onContinue: () => void
}

export function CardStep(
	{ heading, className, organisationId, profile, selectedId, onSelect, onContinue }: CardStepProps,
) {
	const { t } = useTranslation('common')
	const { can } = useAuth()

	const [addCardOpen, setAddCardOpen] = useState(false)
	const [awaitedCard, setAwaitedCard] = useState<{ stripePaymentMethodId: string; until: number } | null>(null)
	const [awaitTimedOut, setAwaitTimedOut] = useState(false)

	// A new card only lands in the list once Stripe's webhook has been mirrored.
	const { data, isLoading } = usePaymentMethodIndex(organisationId, undefined, {
		enabled: !!organisationId,
		refetchInterval: awaitedCard ? POLL_INTERVAL_MS : false,
	})

	const cards = useMemo(() => profileCards(data?.data ?? [], profile), [data, profile])

	useEffect(() => {
		if (!awaitedCard) {
			return
		}

		const added = cards.find((card) => card.stripePaymentMethodId === awaitedCard.stripePaymentMethodId)

		if (added) {
			onSelect(added.id)
			setAwaitedCard(null)
			return
		}

		const timeout = setTimeout(() => {
			setAwaitedCard(null)
			setAwaitTimedOut(true)
		}, Math.max(awaitedCard.until - Date.now(), 0))

		return () => clearTimeout(timeout)
	}, [awaitedCard, cards, onSelect])

	function handleCardAdded(added: AddedPaymentMethod) {
		setAddCardOpen(false)
		setAwaitTimedOut(false)
		setAwaitedCard({ stripePaymentMethodId: added.stripePaymentMethodId, until: Date.now() + POLL_DURATION_MS })
	}

	return (
		<div className={cn('mx-auto max-w-lg space-y-6', className)}>
			{heading ?? (
				<div>
					<h1 className="text-2xl font-bold text-grey-900">{t('subscribe.card.title')}</h1>
					<p className="mt-1 text-sm text-grey-600">{t('subscribe.card.description')}</p>
				</div>
			)}

			{isLoading ? <SubtleLoader delay={0} /> : null}

			{cards.length > 0 && (
				<div className="space-y-2" role="radiogroup" aria-label={t('subscribe.card.title')}>
					{cards.map((card) => (
						<label
							key={card.id}
							className={cn(
								'flex cursor-pointer items-center gap-3 rounded-lg border bg-white p-3 transition-colors',
								card.id === selectedId
									? 'border-brand-500 bg-brand-50'
									: 'border-grey-200 hover:border-grey-300',
							)}
						>
							<input
								type="radio"
								name="payment-method"
								checked={card.id === selectedId}
								onChange={() => onSelect(card.id)}
								className="accent-brand-600"
							/>
							<CreditCardIcon className="h-5 w-5 text-grey-400" />
							<span className="flex-1 text-sm font-medium text-grey-900">
								{describePaymentMethod(card, t)}
							</span>
						</label>
					))}
				</div>
			)}

			{!isLoading && cards.length === 0 && !awaitedCard && (
				<p className="rounded-lg border border-grey-200 bg-grey-50 p-4 text-sm text-grey-600">
					{t('subscribe.card.empty')}
				</p>
			)}

			{awaitedCard
				? (
					<div className="flex items-center gap-2 text-sm text-grey-600">
						<SubtleLoader delay={0} />
						{t('subscribe.card.waiting')}
					</div>
				)
				: null}

			{awaitTimedOut ? <p className="text-sm text-amber-700">{t('subscribe.card.waitTimedOut')}</p> : null}

			<div className="flex items-center justify-between gap-3">
				{can('payment_method:create')
					? (
						<Button
							type="neutral"
							prefix="plus"
							onClick={() => setAddCardOpen(true)}
							disabled={!!awaitedCard}
						>
							{t('subscribe.card.add')}
						</Button>
					)
					: <span className="text-sm text-grey-500">{t('subscribe.card.noPermission')}</span>}
				<Button
					type="committal"
					postfix="arrow"
					onClick={onContinue}
					disabled={!selectedId || !cards.some((card) => card.id === selectedId)}
				>
					{t('subscribe.continue')}
				</Button>
			</div>

			<AddCardDialog
				organisationId={organisationId}
				open={addCardOpen}
				profileId={profile.id}
				onClose={() => setAddCardOpen(false)}
				onCardAdded={handleCardAdded}
			/>
		</div>
	)
}
